import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";

const HAND_STYLE = {
  L: "bg-[#f5d9a8] text-black", R: "bg-[#bfe8c9] text-black", S: "bg-white text-black", "?": "bg-white/20 text-white",
};

// lower = better for pitcher-against numbers
const cellGrade = (v, good, bad, higherBetter = false) => {
  if (v === null || v === undefined) return "bg-white/5 text-neutral-500";
  const isGood = higherBetter ? v >= good : v <= good;
  const isBad = higherBetter ? v <= bad : v >= bad;
  if (isGood) return "bg-emerald-500/25 text-emerald-300";
  if (isBad) return "bg-red-500/25 text-red-300";
  return "bg-yellow-500/10 text-yellow-100";
};
const rankGrade = (r) => (r == null ? "bg-white/5 text-neutral-500" : r <= 10 ? "bg-emerald-500/25 text-emerald-300" : r <= 20 ? "bg-yellow-500/10 text-yellow-100" : "bg-red-500/25 text-red-300");

const RedBar = ({ children }) => (
  <div className="bg-[#8b1526] border border-[#c22040]/40 rounded-t-lg px-3 py-1.5 text-center text-[11px] sm:text-xs font-black uppercase tracking-widest text-white">{children}</div>
);

function SplitBox({ label, s }) {
  const cells = [
    ["wOBA", s?.woba, cellGrade(s?.woba, 0.300, 0.340)],
    ["WHIP", s?.whip, cellGrade(s?.whip, 1.15, 1.40)],
    ["OPS", s?.ops, cellGrade(s?.ops, 0.660, 0.760)],
    ["xFIP", s?.xfip, cellGrade(s?.xfip, 3.6, 4.5)],
  ];
  return (
    <div>
      <RedBar>{label}</RedBar>
      <div className="grid grid-cols-4 border border-t-0 border-white/10 rounded-b-lg overflow-hidden">
        {cells.map(([k]) => (
          <div key={k} className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold text-center py-1 bg-black/50 border-b border-white/10">{k}</div>
        ))}
        {cells.map(([k, v, cls]) => (
          <div key={k + "v"} className={`font-data text-sm font-bold text-center py-1.5 ${cls}`}>{v ?? "—"}</div>
        ))}
      </div>
    </div>
  );
}

function FormRow({ label, f }) {
  const cells = [
    [f?.era, cellGrade(f?.era, 3.2, 4.4)],
    [f?.whip, cellGrade(f?.whip, 1.15, 1.40)],
    [f?.xfip, cellGrade(f?.xfip, 3.6, 4.5)],
    [f?.hardhit != null ? `${f.hardhit}%` : null, cellGrade(f?.hardhit, 33, 39)],
  ];
  return (
    <div>
      <RedBar>{label}</RedBar>
      <div className="grid grid-cols-4 border border-t-0 border-white/10 rounded-b-lg overflow-hidden">
        {["ERA", "WHIP", "xFIP", "HardHit%"].map((k) => (
          <div key={k} className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold text-center py-1 bg-black/50 border-b border-white/10">{k}</div>
        ))}
        {cells.map(([v, cls], i) => (
          <div key={i} className={`font-data text-sm font-bold text-center py-1.5 ${cls}`}>{v ?? "—"}</div>
        ))}
      </div>
    </div>
  );
}

function RankBox({ label, e }) {
  const cells = [["wRC+", e?.wrc_rank], ["OPS", e?.ops_rank], ["wOBA", e?.woba_rank]];
  return (
    <div>
      <RedBar>{label}</RedBar>
      <div className="grid grid-cols-3 border border-t-0 border-white/10 rounded-b-lg overflow-hidden">
        {cells.map(([k]) => (
          <div key={k} className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold text-center py-1 bg-black/50 border-b border-white/10">{k}</div>
        ))}
        {cells.map(([k, r]) => (
          <div key={k + "v"} className={`font-data text-sm font-bold text-center py-1.5 ${rankGrade(r)}`}>{r ?? "—"}</div>
        ))}
      </div>
      <div className="text-[9px] text-neutral-600 text-center mt-0.5">league rank, 1 = best</div>
    </div>
  );
}

function Side({ s, metric, dir }) {
  const p = s.pitcher || {};
  const hand = s.form_ranks?.vs_hand?.hand || p.hand || "R";
  let rows = [...(s.lineup || [])];
  if (metric !== "order") {
    rows.sort((a, b) => {
      const av = a[metric] == null ? -Infinity : Number(a[metric]);
      const bv = b[metric] == null ? -Infinity : Number(b[metric]);
      return dir === "best" ? bv - av : av - bv;
    });
  }
  const fmtM = (v) => {
    if (v == null) return "—";
    if (metric === "avg" || metric === "ops") return Number(v).toFixed(3).replace(/^0/, "");
    if (metric === "heat") return `${v}/10`;
    return `${v}%`;
  };
  return (
    <div className="space-y-4 min-w-0" data-testid={`mlab-side-${s.bat_abbrev}`}>
      {/* Lineup */}
      <div className="card-surface rounded-lg overflow-hidden">
        <RedBar>{s.bat_team} {s.lineup_status === "confirmed" ? "Confirmed" : "Projected"} Lineup</RedBar>
        <div>
          {rows.map((b, ri) => (
            <div key={b.order} className="flex items-center gap-2 px-3 py-1 border-b border-white/5 last:border-0">
              <span className="font-data text-[10px] text-neutral-600 w-4">{metric === "order" ? b.order : ri + 1}</span>
              <span className="flex-1 text-xs sm:text-sm font-semibold uppercase tracking-wide truncate">{b.name}</span>
              {metric !== "order" && (
                <span className={`font-data text-xs font-black w-12 text-right ${ri === 0 && dir === "best" ? "text-[#22c55e]" : ri === 0 ? "text-red-400" : "text-[#a855f7]"}`} data-testid={`mlab-metric-${s.bat_abbrev}-${ri}`}>{fmtM(b[metric])}</span>
              )}
              <span className={`w-7 text-center text-[11px] font-black rounded ${HAND_STYLE[b.bats] || HAND_STYLE["?"]}`}>{b.bats}</span>
            </div>
          ))}
          {rows.length === 0 && <p className="text-xs text-neutral-600 p-3">Lineup data not available yet.</p>}
        </div>
      </div>

      {/* Pitcher splits */}
      <div className="card-surface rounded-lg p-3">
        <div className="flex items-center gap-3 mb-3">
          <img src={p.headshot} alt="" className="w-12 h-12 rounded-full object-cover bg-white/5 border border-[#c22040]/50" loading="lazy" />
          <div>
            <div className="font-display text-base font-bold uppercase">{p.name} <span className="text-neutral-500 text-xs">R/L Splits</span></div>
            <div className="text-[10px] text-neutral-500">{p.team} · {p.hand}HP · faces the {s.bat_team}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SplitBox label="vs Righties" s={p.splits?.vr} />
          <SplitBox label="vs Lefties" s={p.splits?.vl} />
        </div>
      </div>

      {/* Pitcher form */}
      <div className="card-surface rounded-lg p-3 space-y-3">
        <FormRow label="Last 2 Starts" f={p.form?.last2} />
        <FormRow label="Last 5 Starts" f={p.form?.last5} />
        <FormRow label={`Full Season (${p.form?.season?.starts ?? 0} starts)`} f={p.form?.season} />
      </div>

      {/* Team form ranks */}
      <div className="card-surface rounded-lg p-3">
        <div className="grid grid-cols-2 gap-3">
          <RankBox label="Last 14 Days" e={s.form_ranks?.last14} />
          <RankBox label="Last 30 Days" e={s.form_ranks?.last30} />
          <RankBox label="Full Season" e={s.form_ranks?.season} />
          <RankBox label={`vs ${hand}HP (Season)`} e={s.form_ranks?.vs_hand} />
        </div>
      </div>

      {/* Team log vs hand */}
      <div className="card-surface rounded-lg overflow-hidden">
        <RedBar>{s.bat_team} Bats vs {hand}HP Starters</RedBar>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[560px]">
            <thead>
              <tr className="text-neutral-500 text-[10px] uppercase tracking-wider border-b border-white/10">
                <th className="p-2 text-left">Date</th><th className="p-2">Loc</th><th className="p-2 text-left">Pitcher</th>
                <th className="p-2">Rslt</th><th className="p-2">Score</th><th className="p-2">IP</th><th className="p-2">ER</th>
                <th className="p-2">H</th><th className="p-2">BB</th><th className="p-2">xFIP</th>
              </tr>
            </thead>
            <tbody className="font-data">
              {(s.vs_hand_log || []).map((g, i) => (
                <tr key={i} className="border-b border-white/5 text-center">
                  <td className="p-2 text-left font-sans text-neutral-300">{g.date?.slice(5)}</td>
                  <td className="p-2 text-neutral-400">{g.loc}</td>
                  <td className="p-2 text-left font-sans">{g.pitcher} <span className="text-neutral-600">({g.opp})</span></td>
                  <td className={`p-2 font-black ${g.result === "W" ? "text-[#22c55e]" : "text-red-400"}`}>{g.result}</td>
                  <td className="p-2">{g.score}</td>
                  <td className="p-2">{g.ip ?? "—"}</td><td className="p-2">{g.er ?? "—"}</td>
                  <td className="p-2">{g.h ?? "—"}</td><td className="p-2">{g.bb ?? "—"}</td>
                  <td className={`p-2 font-bold ${cellGrade(g.xfip, 4.6, 3.6, true)}`}>{g.xfip ?? "—"}</td>
                </tr>
              ))}
              {(s.vs_hand_log || []).length === 0 && (
                <tr><td colSpan={10} className="p-3 text-neutral-600 font-sans">No recent games vs {hand}HP starters found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-neutral-600 px-2 py-1.5">Green xFIP = weak opposing start (good for bats). Last 10 games vs {hand}HP starters.</p>
      </div>
    </div>
  );
}

export default function MatchupLab() {
  const { data } = useQuery({ queryKey: ["mlabGames"], queryFn: () => api.get("/matchup-lab").then((r) => r.data) });
  const games = data?.games || [];
  const [game, setGame] = useState(null);
  const [metric, setMetric] = useState("order");
  const [dir, setDir] = useState("best");
  useEffect(() => {
    if (!game && games.length > 0) setGame(games[0].game);
  }, [games, game]);
  const { data: detail, isFetching } = useQuery({
    queryKey: ["mlabDetail", game],
    queryFn: () => api.get("/matchup-lab/detail", { params: { game } }).then((r) => r.data),
    enabled: !!game, staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Matchup <span className="gold-text">Lab</span></h1>
        <p className="text-neutral-500 text-sm mt-1">The full pregame sheet for every game: lineups with handedness, starter R/L splits, recent-start form, team bat ranks, and how each lineup has fared against tonight's pitch hand.</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 fade-up-1" data-testid="mlab-game-chips">
        {games.map((g) => (
          <button key={g.game} onClick={() => setGame(g.game)} data-testid={`mlab-chip-${g.game.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
            className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${game === g.game ? "bg-[#8b1526] border-[#c22040] text-white" : "border-white/15 text-neutral-400 hover:border-[#c22040]/60 hover:text-white"}`}>
            {g.game}
          </button>
        ))}
        {games.length === 0 && <p className="text-neutral-500 text-sm">No games with probable starters today. Check back on the next game day.</p>}
      </div>

      {detail?.locked && <UpgradeGate feature="The full Matchup Lab sheet for every game" />}
      {isFetching && !detail && <div className="text-neutral-500 font-data text-sm animate-pulse">Building the matchup sheet (first load takes a few seconds)...</div>}
      {isFetching && detail && <div className="text-neutral-600 font-data text-xs animate-pulse">Refreshing...</div>}

      {detail && !detail.locked && (
        <>
          <div className="flex flex-wrap items-center gap-2 fade-up-1" data-testid="mlab-sort-bar">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Sort lineups:</span>
            {[["order", "Batting Order"], ["heat", "Heat"], ["hr_prob", "HR %"], ["avg", "AVG"], ["ops", "OPS"], ["brl", "Barrel %"], ["hh", "Hard Hit %"]].map(([k, label]) => (
              <button key={k} onClick={() => setMetric(k)} data-testid={`mlab-sort-${k}`}
                className={`text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${metric === k ? "bg-[#a855f7] text-black border-[#a855f7]" : "border-white/15 text-neutral-400 hover:border-[#a855f7]/60 hover:text-[#a855f7]"}`}>
                {label}
              </button>
            ))}
            {metric !== "order" && (
              <button onClick={() => setDir((d) => (d === "best" ? "worst" : "best"))} data-testid="mlab-sort-dir"
                className={`text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${dir === "best" ? "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/50" : "bg-red-500/15 text-red-400 border-red-500/50"}`}>
                {dir === "best" ? "Best → Worst" : "Worst → Best"}
              </button>
            )}
          </div>
          <div className="grid lg:grid-cols-2 gap-5 fade-up-1" data-testid="mlab-detail">
            {detail.sides.map((s) => <Side key={s.bat_team} s={s} metric={metric} dir={dir} />)}
          </div>
        </>
      )}
      <p className="text-[11px] text-neutral-600">wOBA computed from official MLB splits. xFIP and HardHit% are model estimates. Team boxes show league ranks (1 = best offense).</p>
    </div>
  );
}
